import { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  CaretLeft, Camera, CloudSun, MapPin, Plus, X, FileText, Trash,
  Microphone, Stop, Waveform, WifiSlash, MagnifyingGlass, SpinnerGap
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import { MapClickHandler, RecenterMap } from "@/components/MapHelpers";
import { API, templateIcons } from "@/config/constants";
import { compressImage } from "@/utils/imageUtils";
import { saveOfflineEntry } from "@/utils/offline";

const EntryFormPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isEditing = Boolean(id && id !== "new");

  const [formData, setFormData] = useState({
    title: "", content: "", location: "", latitude: "", longitude: "", tags: [], weather: ""
  });
  const [tagInput, setTagInput] = useState("");
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [existingPhotos, setExistingPhotos] = useState([]);
  const [deletingPhoto, setDeletingPhoto] = useState(null);
  const [fetchingWeather, setFetchingWeather] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [showTemplates, setShowTemplates] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [existingVoiceNotes, setExistingVoiceNotes] = useState([]);
  const [showMapPicker, setShowMapPicker] = useState(false);

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const response = await axios.get(`${API}/templates`);
        setTemplates(response.data);
      } catch (error) {
        console.error("Failed to load templates");
      }
    };
    if (!isEditing) fetchTemplates();
  }, [isEditing]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const templateId = params.get('template');
    if (templateId && templates.length > 0) {
      const template = templates.find(t => t.id === templateId);
      if (template) applyTemplate(template);
    }
  }, [location.search, templates]);

  const applyTemplate = (template) => {
    setFormData(prev => ({ ...prev, title: template.title, content: template.content, tags: template.tags }));
    setShowTemplates(false);
    toast.success(`Template "${template.name}" applied`);
  };

  useEffect(() => {
    if (isEditing) {
      const fetchEntry = async () => {
        try {
          const response = await axios.get(`${API}/entries/${id}`);
          const entry = response.data;
          setFormData({
            title: entry.title || "", content: entry.content || "", location: entry.location || "",
            latitude: entry.latitude || "", longitude: entry.longitude || "", tags: entry.tags || [], weather: entry.weather || ""
          });
          setExistingPhotos(entry.photos || []);
          setExistingVoiceNotes(entry.voice_notes || []);
        } catch (error) {
          toast.error("Failed to load entry");
          navigate("/");
        }
      };
      fetchEntry();
    }
  }, [id, isEditing, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({ ...prev, tags: [...prev.tags, tagInput.trim()] }));
      setTagInput("");
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData(prev => ({ ...prev, tags: prev.tags.filter(t => t !== tagToRemove) }));
  };

  const handlePhotoSelect = async (e) => {
    const files = Array.from(e.target.files);
    toast.info(`Compressing ${files.length} image(s)...`);
    const compressedFiles = await Promise.all(files.map(file => compressImage(file, 1200, 0.8)));
    setPhotos(prev => [...prev, ...compressedFiles]);
    toast.success(`${files.length} image(s) compressed and ready`);
  };

  const removePhoto = (index) => { setPhotos(prev => prev.filter((_, i) => i !== index)); };

  const deleteExistingPhoto = async (photoId) => {
    setDeletingPhoto(photoId);
    try {
      await axios.delete(`${API}/entries/${id}/photos/${photoId}`);
      setExistingPhotos(prev => prev.filter(p => p.id !== photoId));
      toast.success("Photo deleted");
    } catch (error) {
      toast.error("Failed to delete photo");
    } finally {
      setDeletingPhoto(null);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      const chunks = [];
      recorder.ondataavailable = (e) => chunks.push(e.data);
      recorder.onstop = () => { const blob = new Blob(chunks, { type: 'audio/webm' }); setAudioBlob(blob); stream.getTracks().forEach(track => track.stop()); };
      recorder.start();
      setMediaRecorder(recorder);
      setIsRecording(true);
      setRecordingTime(0);
    } catch (err) {
      toast.error("Microphone access denied");
    }
  };

  const stopRecording = () => { if (mediaRecorder && mediaRecorder.state !== "inactive") { mediaRecorder.stop(); setIsRecording(false); } };
  const discardRecording = () => { setAudioBlob(null); setRecordingTime(0); };

  useEffect(() => {
    let interval;
    if (isRecording) { interval = setInterval(() => setRecordingTime(prev => prev + 1), 1000); }
    return () => clearInterval(interval);
  }, [isRecording]);

  const handleGetLocation = async () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          setFormData(prev => ({ ...prev, latitude: lat.toString(), longitude: lng.toString() }));
          toast.success("Location captured!");
          setFetchingWeather(true);
          try {
            const weatherRes = await axios.get(`${API}/weather?latitude=${lat}&longitude=${lng}`);
            if (weatherRes.data.condition) {
              setFormData(prev => ({ ...prev, weather: weatherRes.data.condition }));
              toast.success(`Weather auto-filled: ${weatherRes.data.condition} (${weatherRes.data.temperature}${weatherRes.data.temperature_unit})`);
            }
          } catch (error) { console.error("Weather fetch failed:", error); }
          finally { setFetchingWeather(false); }
        },
        () => { toast.error("Could not get location"); }
      );
    }
  };

  const handleFetchWeather = async () => {
    if (!formData.latitude || !formData.longitude) { toast.error("Please set GPS coordinates first"); return; }
    setFetchingWeather(true);
    try {
      const weatherRes = await axios.get(`${API}/weather?latitude=${formData.latitude}&longitude=${formData.longitude}`);
      if (weatherRes.data.condition) {
        setFormData(prev => ({ ...prev, weather: weatherRes.data.condition }));
        toast.success(`Weather: ${weatherRes.data.condition} (${weatherRes.data.temperature}${weatherRes.data.temperature_unit})`);
      }
    } catch (error) { toast.error("Failed to fetch weather"); }
    finally { setFetchingWeather(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.content.trim()) { toast.error("Title and content are required"); return; }
    setSaving(true);
    const entryData = { ...formData, latitude: formData.latitude ? parseFloat(formData.latitude) : null, longitude: formData.longitude ? parseFloat(formData.longitude) : null };
    try {
      let entryId;
      if (isEditing) { await axios.put(`${API}/entries/${id}`, entryData); entryId = id; toast.success("Entry updated!"); }
      else { const response = await axios.post(`${API}/entries`, entryData); entryId = response.data.id; toast.success("Entry created!"); }
      if (photos.length > 0) {
        setUploading(true);
        const photoFormData = new FormData();
        photos.forEach(photo => { photoFormData.append("files", photo); });
        await axios.post(`${API}/entries/${entryId}/photos/bulk`, photoFormData, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success(`${photos.length} photo(s) uploaded`);
      }
      if (audioBlob) {
        const voiceFormData = new FormData();
        voiceFormData.append("file", audioBlob, "voice-note.webm");
        await axios.post(`${API}/entries/${entryId}/voice-notes`, voiceFormData, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Voice note uploaded");
      }
      navigate(`/entry/${entryId}`);
    } catch (error) {
      if (!navigator.onLine) {
        try {
          await saveOfflineEntry(entryData);
          toast.success("Saved offline! Will sync when back online.", { icon: <WifiSlash size={20} /> });
          if ('serviceWorker' in navigator && 'sync' in window.registration) { const registration = await navigator.serviceWorker.ready; await registration.sync.register('sync-offline-entries'); }
          navigate("/");
        } catch (offlineError) { toast.error("Failed to save entry offline"); }
      } else { toast.error("Failed to save entry"); }
    } finally { setSaving(false); setUploading(false); }
  };

  return (
    <div className="max-w-3xl mx-auto page-content" data-testid="entry-form-page">
      <div className="flex flex-wrap items-center gap-2 sm:gap-4 mb-6">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="text-[#55624F]"><CaretLeft size={18} />Back</Button>
        <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-white">{isEditing ? "Edit Entry" : "New Entry"}</h1>
        {!isEditing && (
          <Dialog open={showTemplates} onOpenChange={setShowTemplates}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" className="ml-auto border-[#D2C2B3] text-[#55624F] text-xs sm:text-sm" data-testid="use-template-btn"><FileText size={16} className="mr-1" />Template</Button>
            </DialogTrigger>
            <DialogContent className="bg-white border-[#D2C2B3] max-w-lg">
              <DialogHeader><DialogTitle className="font-heading text-xl">Choose a Template</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3 mt-4">
                {templates.map(template => {
                  const IconComponent = templateIcons[template.icon] || FileText;
                  return (
                    <Card key={template.id} className="cursor-pointer hover:bg-[#EAE7DE] transition-colors border-[#D2C2B3]" onClick={() => applyTemplate(template)} data-testid={`template-${template.id}`}>
                      <CardContent className="p-4 flex items-center gap-3">
                        <div className="p-2 bg-[#EAE7DE] rounded-lg"><IconComponent size={24} weight="duotone" className="text-[#55624F]" /></div>
                        <span className="font-medium text-[#2E332B]">{template.name}</span>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-[#55624F] mb-2">Title *</label>
          <Input name="title" value={formData.title} onChange={handleInputChange} placeholder="What did you observe today?" className="bg-[#EAE7DE] border-[#D2C2B3] focus:border-[#55624F]" data-testid="entry-title-input" />
        </div>
        <div>
          <label className="block text-sm font-medium text-[#55624F] mb-2">Journal Entry *</label>
          <Textarea name="content" value={formData.content} onChange={handleInputChange} placeholder="Describe your nature observation..." rows={6} className="bg-[#EAE7DE] border-[#D2C2B3] focus:border-[#55624F]" data-testid="entry-content-input" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-[#55624F] mb-2">Location Name</label>
            <Input name="location" value={formData.location} onChange={handleInputChange} placeholder="e.g., Central Park" className="bg-[#EAE7DE] border-[#D2C2B3] focus:border-[#55624F]" data-testid="entry-location-input" />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#55624F] mb-2">Weather</label>
            <div className="flex gap-2">
              <Select value={formData.weather} onValueChange={(value) => setFormData(prev => ({ ...prev, weather: value }))}>
                <SelectTrigger className="flex-1 bg-[#EAE7DE] border-[#D2C2B3]" data-testid="entry-weather-select"><SelectValue placeholder="Select weather" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="sunny">Sunny</SelectItem><SelectItem value="cloudy">Cloudy</SelectItem><SelectItem value="rainy">Rainy</SelectItem>
                  <SelectItem value="snowy">Snowy</SelectItem><SelectItem value="windy">Windy</SelectItem><SelectItem value="foggy">Foggy</SelectItem>
                </SelectContent>
              </Select>
              <Button type="button" onClick={handleFetchWeather} disabled={fetchingWeather || (!formData.latitude && !formData.longitude)} variant="outline" className="border-[#D2C2B3] text-[#55624F]" title="Auto-fill from GPS" data-testid="fetch-weather-btn">
                {fetchingWeather ? <span className="animate-pulse-soft">...</span> : <CloudSun size={20} weight="duotone" />}
              </Button>
            </div>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-[#55624F] mb-2">GPS Coordinates</label>
          <div className="flex flex-wrap gap-2 sm:gap-4 mb-2">
            <Input name="latitude" value={formData.latitude} onChange={handleInputChange} placeholder="Latitude" className="flex-1 min-w-[100px] bg-[#EAE7DE] border-[#D2C2B3] focus:border-[#55624F]" data-testid="entry-latitude-input" />
            <Input name="longitude" value={formData.longitude} onChange={handleInputChange} placeholder="Longitude" className="flex-1 min-w-[100px] bg-[#EAE7DE] border-[#D2C2B3] focus:border-[#55624F]" data-testid="entry-longitude-input" />
            <div className="flex gap-2">
              <Button type="button" onClick={handleGetLocation} variant="outline" className="border-[#D2C2B3] text-[#55624F]" data-testid="get-location-btn" title="Use current GPS location"><MapPin size={20} /></Button>
              <Button type="button" onClick={() => setShowMapPicker(prev => !prev)} variant="outline" className={`border-[#D2C2B3] ${showMapPicker ? 'bg-[#55624F] text-white' : 'text-[#55624F]'}`} data-testid="toggle-map-picker-btn" title="Pick location on map"><MagnifyingGlass size={20} /></Button>
            </div>
          </div>
          {showMapPicker && (
            <div className="h-64 rounded-lg overflow-hidden border border-[#D2C2B3] mt-2" data-testid="map-picker">
              <MapContainer center={formData.latitude && formData.longitude ? [parseFloat(formData.latitude), parseFloat(formData.longitude)] : [40.0, -95.0]} zoom={formData.latitude ? 13 : 3} className="h-full w-full">
                <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                <MapClickHandler onClick={(latlng) => { setFormData(prev => ({ ...prev, latitude: latlng.lat.toFixed(6), longitude: latlng.lng.toFixed(6) })); toast.success(`Location set: ${latlng.lat.toFixed(4)}, ${latlng.lng.toFixed(4)}`); }} />
                <RecenterMap lat={formData.latitude ? parseFloat(formData.latitude) : null} lng={formData.longitude ? parseFloat(formData.longitude) : null} zoom={13} />
                {formData.latitude && formData.longitude && (<Marker position={[parseFloat(formData.latitude), parseFloat(formData.longitude)]}><Popup>Selected location</Popup></Marker>)}
              </MapContainer>
              <p className="text-xs text-[#7C8068] mt-1 text-center">Click on the map to set coordinates</p>
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-[#55624F] mb-2">Tags</label>
          <div className="flex gap-2 mb-2">
            <Input value={tagInput} onChange={(e) => setTagInput(e.target.value)} placeholder="Add a tag" onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), handleAddTag())} className="flex-1 bg-[#EAE7DE] border-[#D2C2B3] focus:border-[#55624F]" data-testid="tag-input" />
            <Button type="button" onClick={handleAddTag} variant="outline" className="border-[#D2C2B3] text-[#55624F]" data-testid="add-tag-btn"><Plus size={20} /></Button>
          </div>
          {formData.tags.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {formData.tags.map(tag => (<Badge key={tag} className="bg-[#EAE7DE] text-[#2E332B] cursor-pointer hover:bg-[#D2C2B3]" onClick={() => handleRemoveTag(tag)}>{tag}<X size={14} className="ml-1" /></Badge>))}
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-[#55624F] mb-2">Photos</label>
          <div className="dropzone" onClick={() => document.getElementById("photo-input").click()}>
            <Camera size={40} className="mx-auto mb-2 text-[#7C8068]" weight="duotone" />
            <p className="text-[#7C8068]">Click to upload photos</p>
            <p className="text-sm text-[#7C8068]">JPG, PNG, WEBP supported</p>
            <input id="photo-input" type="file" multiple accept="image/*" onChange={handlePhotoSelect} className="hidden" data-testid="photo-upload-input" />
          </div>
          {existingPhotos.length > 0 && (
            <div className="mt-4">
              <p className="text-sm text-[#7C8068] mb-2">Existing photos:</p>
              <div className="grid grid-cols-4 gap-2">
                {existingPhotos.map(photo => (
                  <div key={photo.id} className="relative group">
                    <img src={`${API}/photos/${photo.storage_path}`} alt={photo.original_filename} className="w-full h-20 object-contain rounded-lg border border-[#D2C2B3] bg-[#EAE7DE]" />
                    <button
                      type="button"
                      onClick={() => deleteExistingPhoto(photo.id)}
                      disabled={deletingPhoto === photo.id}
                      className="absolute -top-2 -right-2 bg-[#C25A3C] text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50"
                      data-testid={`delete-existing-photo-${photo.id}`}
                    >
                      {deletingPhoto === photo.id ? <SpinnerGap size={12} className="animate-spin" /> : <X size={12} />}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          {photos.length > 0 && (
            <div className="mt-4">
              <p className="text-sm text-[#7C8068] mb-2">New photos to upload:</p>
              <div className="grid grid-cols-4 gap-2">
                {photos.map((photo, index) => (
                  <div key={index} className="relative">
                    <img src={URL.createObjectURL(photo)} alt={`Preview ${index}`} className="w-full h-20 object-cover rounded-lg border border-[#D2C2B3]" />
                    <button type="button" onClick={() => removePhoto(index)} className="absolute -top-2 -right-2 bg-[#C25A3C] text-white rounded-full p-1"><X size={12} /></button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-[#55624F] mb-2">Voice Note</label>
          <div className="bg-[#EAE7DE] border border-[#D2C2B3] rounded-lg p-4">
            {!audioBlob ? (
              <div className="flex items-center gap-4">
                <button type="button" onClick={isRecording ? stopRecording : startRecording} className={`w-14 h-14 rounded-full flex items-center justify-center transition-all ${isRecording ? "bg-[#C25A3C] text-white animate-pulse" : "bg-[#55624F] text-white hover:bg-[#424D3D]"}`} data-testid="record-btn">
                  {isRecording ? <Stop size={24} weight="fill" /> : <Microphone size={24} weight="fill" />}
                </button>
                <div className="flex-1">
                  {isRecording ? (
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 rounded-full bg-[#C25A3C] animate-pulse" />
                      <span className="text-[#C25A3C] font-medium text-sm">Recording... {Math.floor(recordingTime / 60)}:{(recordingTime % 60).toString().padStart(2, '0')}</span>
                      <div className="flex-1 flex items-center gap-0.5">
                        {[...Array(20)].map((_, i) => (<div key={i} className="w-1 bg-[#C25A3C] rounded-full animate-pulse" style={{ height: `${Math.random() * 16 + 4}px`, animationDelay: `${i * 50}ms` }} />))}
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-[#7C8068]">Tap to record a voice note for this entry</p>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <Waveform size={20} weight="duotone" className="text-[#55624F]" />
                <audio controls src={URL.createObjectURL(audioBlob)} className="flex-1 h-10" data-testid="audio-preview" />
                <button type="button" onClick={discardRecording} className="p-2 text-[#C25A3C] hover:bg-[#D2C2B3] rounded-lg transition-colors" data-testid="discard-recording-btn"><Trash size={18} /></button>
              </div>
            )}
          </div>
          {existingVoiceNotes.length > 0 && (
            <div className="mt-3 space-y-2">
              <p className="text-sm text-[#7C8068]">Existing voice notes:</p>
              {existingVoiceNotes.map(vn => (
                <div key={vn.id} className="flex items-center gap-2 bg-white p-2 rounded-lg border border-[#D2C2B3]">
                  <Waveform size={16} className="text-[#55624F]" />
                  <audio controls src={`${API}/voice/${vn.storage_path}`} className="flex-1 h-8" />
                  {vn.transcription && (<span className="text-xs text-[#7C8068] truncate max-w-[200px]" title={vn.transcription}>{vn.transcription}</span>)}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex gap-4">
          <Button type="button" variant="outline" onClick={() => navigate(-1)} className="flex-1 border-[#D2C2B3] text-[#55624F]">Cancel</Button>
          <Button type="submit" disabled={saving || uploading} className="flex-1 bg-[#55624F] hover:bg-[#424D3D] text-white" data-testid="save-entry-btn">
            {saving ? "Saving..." : uploading ? "Uploading photos..." : isEditing ? "Update Entry" : "Create Entry"}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default EntryFormPage;
